import React from 'react';
import styled from 'styled-components';

import { DataElementContext } from '@/page-components/common/DataElementContext';
import { processComponentProps } from '@/page-components/utils/processComponentProps';

import {
  generateNextLiveStateG1,
  generateNextLiveStateG2,
  generateNextLiveStateG3,
  generateNextLiveStateG4,
  getMockData,
  GROUP_INTERVALS,
  initialLiveStateG1,
  initialLiveStateG2,
  initialLiveStateG3,
  initialLiveStateG4,
  LiveStateG1,
  LiveStateG2,
  LiveStateG3,
  LiveStateG4,
} from './mockData';

//import './index.scss';

type StatsProps = {
  children: any;
  styleText: string;
  className: string;
  properties?: any;
};

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

const Stats = (componentProps: StatsProps) => {
  let props = componentProps;

  const dataElementContext = React.useContext(DataElementContext);
  [props] = processComponentProps(props, dataElementContext);

  const [state, setState] = React.useState({
    selectedTimeRange: {
      metrics1: '7d',
      metrics2: '7d',
      metrics3: '7d',
      metrics4: '7d',
      eventsTable1: '7d',
    },
    // Per-group live + prev state slices
    g1Live: initialLiveStateG1 as LiveStateG1,
    g1Prev: initialLiveStateG1 as LiveStateG1,
    g2Live: initialLiveStateG2 as LiveStateG2,
    g2Prev: initialLiveStateG2 as LiveStateG2,
    g3Live: initialLiveStateG3 as LiveStateG3,
    g3Prev: initialLiveStateG3 as LiveStateG3,
    g4Live: initialLiveStateG4 as LiveStateG4,
    g4Prev: initialLiveStateG4 as LiveStateG4,
    events: [
      {
        className: 'row-anim',
        data: [
          { label: 'Halloween Poking' },
          { label: 'Monetization' },
          { label: 'Churn prevention' },
          { label: 'Push' },
          { label: 'Found 20 users that ...' },
          { label: 'Executed', status: 'success' },
        ],
      },
      {
        className: 'row-anim',
        data: [
          { label: 'Football Master Promo' },
          { label: 'Retention' },
          { label: 'Activation' },
          { label: 'SMS' },
          { label: 'Found 12 online users' },
          { label: 'Missed', status: 'error' },
        ],
      },
      {
        className: 'row-anim',
        data: [
          { label: 'Copa Talento Futsal' },
          { label: 'Retention' },
          { label: 'Acquisition' },
          { label: 'Mail' },
          { label: 'Reasoning football event' },
          { label: 'Expired', status: 'error' },
        ],
      },
      {
        className: 'row-anim',
        data: [
          { label: 'Lorem Ipsum Dolor' },
          { label: 'Conversion' },
          { label: 'Retention' },
          { label: 'Mail' },
          { label: 'Found 12 online users' },
          { label: 'Executed', status: 'success' },
        ],
      },
    ],
  });

  // Group 1 – Acquisition (20 s)
  React.useEffect(() => {
    let mounted = true;
    const tid = setInterval(() => {
      if (!mounted) return;
      setState((prev) => {
        const next = generateNextLiveStateG1(prev.g1Live);
        return { ...prev, g1Prev: prev.g1Live, g1Live: next };
      });
    }, GROUP_INTERVALS.group1);
    return () => {
      mounted = false;
      clearInterval(tid);
    };
  }, []);

  // Group 2 – Financials (15 s)
  React.useEffect(() => {
    let mounted = true;
    const tid = setInterval(() => {
      if (!mounted) return;
      setState((prev) => {
        const next = generateNextLiveStateG2(prev.g2Live);
        return { ...prev, g2Prev: prev.g2Live, g2Live: next };
      });
    }, GROUP_INTERVALS.group2);
    return () => {
      mounted = false;
      clearInterval(tid);
    };
  }, []);

  // Group 3 – Live KPIs (10 s)
  React.useEffect(() => {
    let mounted = true;
    const tid = setInterval(() => {
      if (!mounted) return;
      setState((prev) => {
        const next = generateNextLiveStateG3(prev.g3Live);
        return { ...prev, g3Prev: prev.g3Live, g3Live: next };
      });
    }, GROUP_INTERVALS.group3);
    return () => {
      mounted = false;
      clearInterval(tid);
    };
  }, []);

  // Group 4 – Top Charts (30 s)
  React.useEffect(() => {
    let mounted = true;
    const tid = setInterval(() => {
      if (!mounted) return;
      setState((prev) => {
        const next = generateNextLiveStateG4(prev.g4Live);
        return { ...prev, g4Prev: prev.g4Live, g4Live: next };
      });
    }, GROUP_INTERVALS.group4);
    return () => {
      mounted = false;
      clearInterval(tid);
    };
  }, []);

  // Events table animation (3 s)
  React.useEffect(() => {
    let mounted = true;
    const tid = setInterval(() => {
      if (!mounted) return;
      setState((prevState) => {
        const events = [...prevState.events];
        const lastEvent = events.pop();
        if (lastEvent) {
          events.unshift({ ...lastEvent });
          events[0].className = '';
        }
        setTimeout(() => {
          if (!mounted) return;
          setState((prevState2) => {
            const events2 = [...prevState2.events];
            events2[0] = { ...events2[0], className: 'row-anim' };
            return { ...prevState2, events: events2 };
          });
        }, 100);
        return { ...prevState, events };
      });
    }, 3000);
    return () => {
      clearInterval(tid);
      mounted = false;
    };
  }, []);

  const onTimeRangeChange = React.useCallback((e: React.MouseEvent<HTMLElement>) => {
    const metricsId = e?.currentTarget?.dataset?.id ?? null;
    const timeRangeValue = e?.currentTarget?.dataset?.value ?? null;
    if (
      metricsId &&
      ['eventsTable1', 'metrics1', 'metrics2', 'metrics3', 'metrics4'].includes(metricsId) &&
      timeRangeValue
    ) {
      setState((prevState) => ({
        ...prevState,
        selectedTimeRange: { ...prevState.selectedTimeRange, [metricsId]: timeRangeValue },
      }));
    }
  }, []);

  const contextValue = React.useMemo(() => {
    const timeRange = [
      { label: '1D', value: '1d' },
      { label: '7D', value: '7d' },
      { label: '1M', value: '1m' },
      { label: '6M', value: '6m' },
      { label: '1Y', value: '1y' },
    ];

    return {
      ...getMockData(
        timeRange,
        onTimeRangeChange,
        state.selectedTimeRange,
        state.g1Live,
        state.g1Prev,
        state.g2Live,
        state.g2Prev,
        state.g3Live,
        state.g3Prev,
        state.g4Live,
        state.g4Prev,
      ),
      actions: {},
      parentContext: dataElementContext,
    };
  }, [
    dataElementContext,
    state.selectedTimeRange,
    state.g1Live,
    state.g1Prev,
    state.g2Live,
    state.g2Prev,
    state.g3Live,
    state.g3Prev,
    state.g4Live,
    state.g4Prev,
    onTimeRangeChange,
  ]);

  console.log('oh.module.stats[contextValue]', contextValue);

  return (
    <ModuleElementDiv className={props.className ?? ''} $styleText={props.styleText}>
      <DataElementContext.Provider value={contextValue}>{componentProps.children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default Stats;
